import React, { useEffect, useRef, useState } from 'react';
import GooglePayButton from '@google-pay/button-react';

const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://us-central1-fotocarnetmadrid.cloudfunctions.net';
const GOOGLE_PAY_GATEWAY = 'paycomet';
const GOOGLE_PAY_GATEWAY_MERCHANT_ID = (
  process.env.REACT_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID || ''
).trim();
const GOOGLE_PAY_MERCHANT_ID = (
  process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || ''
).trim();
const GOOGLE_PAY_MERCHANT_NAME =
  process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME || 'fotopacasa.es';
const GOOGLE_PAY_COUNTRY_CODE = (
  process.env.REACT_APP_GOOGLE_PAY_COUNTRY_CODE || 'ES'
)
  .trim()
  .toUpperCase();
const GOOGLE_PAY_ALLOWED_CARD_NETWORKS = (
  process.env.REACT_APP_GOOGLE_PAY_ALLOWED_CARD_NETWORKS ||
  'MASTERCARD,VISA'
)
  .split(',')
  .map((value) => value.trim().toUpperCase())
  .filter(Boolean);
const GOOGLE_PAY_ALLOWED_AUTH_METHODS = (
  process.env.REACT_APP_GOOGLE_PAY_ALLOWED_AUTH_METHODS ||
  'PAN_ONLY,CRYPTOGRAM_3DS'
)
  .split(',')
  .map((value) => value.trim().toUpperCase())
  .filter(Boolean);

function getGooglePayEnvironment() {
  const configured = (
    process.env.REACT_APP_GOOGLE_PAY_ENVIRONMENT || ''
  ).trim().toUpperCase();

  if (configured === 'TEST' || configured === 'PRODUCTION') {
    return configured;
  }

  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    return 'TEST';
  }

  return 'PRODUCTION';
}

function getTotalPrice(amount) {
  const value = Number.parseFloat(String(amount));
  if (!Number.isFinite(value) || value <= 0) {
    return '5.00';
  }

  return value.toFixed(2);
}

function getAmountCents(amount) {
  return String(Math.round(Number.parseFloat(String(amount)) * 100) || 500);
}

function buildErrorMessage(data, response) {
  if (data && typeof data.error === 'string') {
    const errorCode =
      data.details &&
      typeof data.details === 'object' &&
      data.details.errorCode !== undefined
        ? ` (${data.details.errorCode})`
        : '';
    return `${data.error}${errorCode}`;
  }

  return `Error del servidor: ${response.status}`;
}

async function submitGooglePayPayment({ paymentToken, amount, currency }) {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/handleGooglePayPayment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      paymentToken,
      amount: getAmountCents(amount),
      currency,
      productDescription: 'fotopacasa.es',
      returnOrigin: window.location.origin,
    }),
  });

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok || !data || data.error) {
    throw new Error(buildErrorMessage(data, response));
  }

  return data;
}

const GooglePayOption = ({
  amount = '5.00',
  currency = 'EUR',
  description = 'Paga rapido y seguro con Google Pay.',
  dividerLabel = 'O pagar con tarjeta',
  onSuccess,
  onError,
}) => {
  const [error, setError] = useState(null);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const environment = getGooglePayEnvironment();
  const isConfigured =
    Boolean(GOOGLE_PAY_GATEWAY_MERCHANT_ID) &&
    (environment === 'TEST' || Boolean(GOOGLE_PAY_MERCHANT_ID));

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onError, onSuccess]);

  useEffect(() => {
    const handleReturnMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const payload = event.data || {};
      if (payload.source !== 'paycomet-embed') {
        return;
      }

      if (payload.type === 'success') {
        setError(null);
        setIsProcessing(false);
        if (onSuccessRef.current) {
          onSuccessRef.current(payload.data);
        }
        return;
      }

      if (payload.type === 'error') {
        const challengeError = new Error(
          (payload.data && payload.data.message) ||
            'El pago con Google Pay no se ha completado.'
        );
        setError(challengeError.message);
        setIsProcessing(false);
        if (onErrorRef.current) {
          onErrorRef.current(challengeError);
        }
      }
    };

    window.addEventListener('message', handleReturnMessage);
    return () => window.removeEventListener('message', handleReturnMessage);
  }, []);

  if (!isConfigured) {
    return null;
  }

  const merchantInfo =
    environment === 'PRODUCTION'
      ? {
          merchantId: GOOGLE_PAY_MERCHANT_ID,
          merchantName: GOOGLE_PAY_MERCHANT_NAME,
        }
      : {
          merchantName: GOOGLE_PAY_MERCHANT_NAME,
        };

  const paymentRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    merchantInfo,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: GOOGLE_PAY_ALLOWED_AUTH_METHODS,
          allowedCardNetworks: GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: GOOGLE_PAY_GATEWAY,
            gatewayMerchantId: GOOGLE_PAY_GATEWAY_MERCHANT_ID,
          },
        },
      },
    ],
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPriceLabel: 'Total',
      totalPrice: getTotalPrice(amount),
      currencyCode: currency,
      countryCode: GOOGLE_PAY_COUNTRY_CODE,
    },
    emailRequired: true,
    checkoutOption: 'COMPLETE_IMMEDIATE_PURCHASE',
  };

  return (
    <div
      className="payment-google-pay"
      style={{
        display: buttonVisible || isProcessing || error ? 'block' : 'none',
      }}
    >
      <p className="payment-copy">{description}</p>
      <div
        className="payment-google-pay-button"
        style={{
          pointerEvents: isProcessing ? 'none' : 'auto',
          opacity: isProcessing ? 0.7 : 1,
        }}
      >
        <GooglePayButton
          environment={environment}
          buttonType="pay"
          buttonColor="black"
          buttonRadius={23}
          buttonSizeMode="fill"
          buttonLocale="es"
          existingPaymentMethodRequired={environment === 'PRODUCTION'}
          paymentRequest={paymentRequest}
          style={{ width: '100%', height: '46px' }}
          onClick={() => {
            setError(null);
          }}
          onCancel={() => {
            setIsProcessing(false);
          }}
          onError={(reason) => {
            setIsProcessing(false);

            if (reason && reason.statusCode === 'CANCELED') {
              return;
            }

            const googlePayError = new Error(
              'No se ha podido iniciar Google Pay.'
            );
            setError(googlePayError.message);
            if (onErrorRef.current) {
              onErrorRef.current(googlePayError);
            }
          }}
          onReadyToPayChange={(result) => {
            setButtonVisible(
              Boolean(result && result.isButtonVisible && result.isReadyToPay)
            );
          }}
          onLoadPaymentData={async (paymentData) => {
            setIsProcessing(true);
            setError(null);

            try {
              const paymentToken =
                paymentData &&
                paymentData.paymentMethodData &&
                paymentData.paymentMethodData.tokenizationData &&
                paymentData.paymentMethodData.tokenizationData.token;

              if (!paymentToken) {
                throw new Error('Google Pay no devolvio un token valido.');
              }

              const result = await submitGooglePayPayment({
                paymentToken,
                amount,
                currency,
              });

              const challengeUrl =
                result.challengeUrl || result.challengeURL || '';

              if (challengeUrl) {
                const challengeWindow = window.open(
                  challengeUrl,
                  'paycometGooglePayChallenge',
                  'popup=yes,width=480,height=720'
                );

                if (!challengeWindow) {
                  throw new Error(
                    'No se ha podido abrir la verificacion segura. Permite ventanas emergentes e intentalo de nuevo.'
                  );
                }

                challengeWindow.focus();
                setIsProcessing(false);
                return;
              }

              setIsProcessing(false);
              if (onSuccessRef.current) {
                onSuccessRef.current({
                  paymentMethod: 'GOOGLE_PAY',
                  amount,
                  currency,
                  status: 'completed',
                  gatewayResponse: result,
                });
              }
            } catch (err) {
              const message =
                (err && err.message) ||
                'No se ha podido completar el pago con Google Pay.';
              setError(message);
              setIsProcessing(false);
              if (onErrorRef.current) {
                onErrorRef.current(new Error(message));
              }
            }
          }}
        />
      </div>

      {error && <p className="payment-inline-error">{error}</p>}

      <div className="payment-divider" aria-hidden="true">
        <span>{dividerLabel}</span>
      </div>
    </div>
  );
};

export default GooglePayOption;
